import { observer, useLocalStore } from 'mobx-react-lite';
import React, { useContext } from 'react';
import { ChatsContext } from '../../contexts/Chats';
import { SaveChangesContext } from '../../contexts/SaveChanges';
import { TopicsContext } from '../../contexts/Topics';
import { UsersContext } from '../../contexts/Users';
import { useWindowSize } from '../../hooks/useWindowSize';
import { getBreadcrumbOrigin } from '../../navigation/breadcrumbOrigin';
import { getBreadcrumbs } from '../../navigation/breadcrumbs';
import { CurrentPath } from '../../navigation/routerAdapter';
import * as CSS from './elements/Breadcrumbs';

// on small screens long trails show the first crumb, an expand button and the last two
const COLLAPSE_AFTER = 3;

const BreadcrumbTrail = observer(({ pathname }) => {
  const usersStore = useContext(UsersContext);
  const chatsStore = useContext(ChatsContext);
  const topicsStore = useContext(TopicsContext);
  const saveChangesStore = useContext(SaveChangesContext);
  const windowStore = useWindowSize();
  const store = useLocalStore(() => ({
    expandedPath: null,
    expand(path) {
      store.expandedPath = path;
    },
  }));

  const crumbs = getBreadcrumbs(pathname, {
    usersStore,
    chatsStore,
    topicsStore,
    loggedInUser: usersStore.getLoggedInUser(),
    origin: getBreadcrumbOrigin(),
  });
  if (crumbs.length < 2) return null;

  const isCollapsed =
    windowStore.isMobile &&
    crumbs.length > COLLAPSE_AFTER &&
    store.expandedPath !== pathname;
  const hiddenCount = isCollapsed ? crumbs.length - COLLAPSE_AFTER : 0;

  return (
    <CSS.BreadcrumbNav aria-label="Breadcrumb" data-testid="Breadcrumbs">
      <CSS.BreadcrumbList>
        {crumbs.map(({ to, label }, i) => {
          const isCurrent = i === crumbs.length - 1;
          const isHidden = i > 0 && i <= hiddenCount;
          if (isHidden && i > 1) return null;
          return (
            <CSS.BreadcrumbItem
              key={to}
              isCurrent={isCurrent}
              isFirst={i === 0}
            >
              {i > 0 && <CSS.Separator />}
              {isHidden ? (
                <CSS.ExpandButton
                  type="button"
                  aria-label={`Show ${hiddenCount} more`}
                  onClick={() => store.expand(pathname)}
                >
                  …
                </CSS.ExpandButton>
              ) : isCurrent ? (
                <CSS.CurrentCrumb aria-current="page" title={label}>
                  {label}
                </CSS.CurrentCrumb>
              ) : (
                <CSS.BreadcrumbLink
                  to={to}
                  title={label}
                  onClick={(e) =>
                    saveChangesStore.handleNavigate(
                      e,
                      windowStore.isMobile,
                      chatsStore
                    )
                  }
                >
                  {label}
                </CSS.BreadcrumbLink>
              )}
            </CSS.BreadcrumbItem>
          );
        })}
      </CSS.BreadcrumbList>
    </CSS.BreadcrumbNav>
  );
});

const Breadcrumbs = () => (
  <CurrentPath>
    {(pathname) => <BreadcrumbTrail pathname={pathname} />}
  </CurrentPath>
);

export default Breadcrumbs;
